// File: src/components/admin/AdminEventLocation.jsx
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

export default function AdminEventLocation({ address, onAddressChange, value, onResolved, onBusyChange }) {
  const [markets, setMarkets] = useState([]);
  const [newMarketName, setNewMarketName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  const api = process.env.REACT_APP_API_URL;
  const config = () => ({ headers: {
    Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}`,
  } });

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  useEffect(() => {
    mountedRef.current = true;
    const controller = new AbortController();
    axios.get(`${api}/admin/markets`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
      signal: controller.signal,
    }).then(({ data }) => setMarkets(Array.isArray(data) ? data : []))
      .catch(err => {
        if (!controller.signal.aborted) {
          setError(err.response?.data?.message || 'Could not load markets. Try Lookup address again.');
        }
      });
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
      controller.abort();
    };
  }, [api]);

  // Invalidate pending responses when another event or address is loaded.
  useEffect(() => {
    requestRef.current += 1;
    setBusy(false);
    setError('');
    setNewMarketName('');
  }, [address]);

  const run = async task => {
    const request = ++requestRef.current;
    setBusy(true);
    setError('');
    try {
      await task(() => mountedRef.current && request === requestRef.current);
    } catch (err) {
      if (mountedRef.current && request === requestRef.current) {
        setError(err.response?.data?.message || err.message);
      }
    } finally {
      if (mountedRef.current && request === requestRef.current) setBusy(false);
    }
  };

  const lookup = () => run(async current => {
    const lookupAddress = address.trim();
    if (!lookupAddress) throw new Error('Enter an address first.');
    onResolved(null);
    const { data } = await axios.post(`${api}/admin/markets/resolve-address`,
      { address: lookupAddress }, config());
    if (!current()) return;
    if (!Number.isFinite(data.latitude) || !Number.isFinite(data.longitude) || !data.timeZone) {
      throw new Error('Could not resolve coordinates and timezone. Check the address.');
    }
    if (data.status === 'needs_place') {
      throw new Error('Could not identify the city/locality. Try a more specific address.');
    }
    onResolved({
      address: lookupAddress,
      latitude: data.latitude,
      longitude: data.longitude,
      timeZone: data.timeZone,
      place: data.place,
      marketId: data.market?._id || '',
      market: data.market || null,
      mappedMarketId: data.market?._id || null,
      mappingKnown: true,
    });
    const response = await axios.get(`${api}/admin/markets`, config());
    if (current()) setMarkets(Array.isArray(response.data) ? response.data : []);
  });

  const createMarket = () => run(async current => {
    const name = newMarketName.trim();
    if (!name) throw new Error('Enter a market name.');
    const { data: market } = await axios.post(`${api}/admin/markets`, { name }, config());
    if (!current()) return;
    setMarkets(previous => [...previous.filter(item => item._id !== market._id), market]);
    setNewMarketName('');
    onResolved({ ...value, marketId: market._id, market });
  });

  const remember = () => run(async current => {
    if (!value?.mappingKnown) throw new Error('Lookup the address first before saving a place grouping.');
    await axios.post(`${api}/admin/markets/${value.marketId}/places`, {
      latitude: value.latitude,
      longitude: value.longitude,
      expectedMarketId: value.mappedMarketId || null,
    }, config());
    if (current()) onResolved({ ...value, mappedMarketId: value.marketId });
  });

  const selectedMarket = value?.market || markets.find(item => item._id === value?.marketId);
  const placeLabel = [value?.place?.locality, value?.place?.county,
    value?.place?.state, value?.place?.countryCode].filter(Boolean).join(', ');
  const validLocation = value && value.address === address.trim();

  return (
    <fieldset style={{ margin: '12px 0', padding: 12 }}>
      <legend>Event location</legend>
      <label>
        Address: {' '}
        <input value={address} onChange={event => {
          requestRef.current += 1;
          setBusy(false);
          onAddressChange(event.target.value);
          onResolved(null);
        }} />
      </label>{' '}
      <button type="button" disabled={busy || !address.trim()} onClick={lookup}>
        {busy ? 'Working…' : 'Lookup address'}
      </button>
      {validLocation && (
        <>
          <p>{placeLabel || 'Location resolved'}<br />
            Coordinates: {value.latitude}, {value.longitude}<br />
            Timezone: {value.timeZone}</p>
          <label>Market: {' '}
            <select value={value.marketId || ''} disabled={busy} onChange={event => {
              const marketId = event.target.value;
              onResolved({ ...value, marketId,
                market: markets.find(item => item._id === marketId) || null });
            }}>
              <option value="">Choose a market</option>
              {selectedMarket && !markets.some(item => item._id === selectedMarket._id) && (
                <option value={selectedMarket._id}>{selectedMarket.name || selectedMarket._id}</option>
              )}
              {markets.map(market => <option key={market._id} value={market._id}>{market.name}</option>)}
            </select>
          </label>
          {!value.marketId && <p>This place needs a market. Choose one or create one below.</p>}
          <div style={{ marginTop: 8 }}>
            <label>New market name: {' '}
              <input value={newMarketName} disabled={busy}
                onChange={event => setNewMarketName(event.target.value)} placeholder="Los Angeles" />
            </label>{' '}
            <button type="button" disabled={busy || !newMarketName.trim()} onClick={createMarket}>
              Create market
            </button>
          </div>
          {value.marketId && value.place?.locality && (
            <div style={{ marginTop: 8 }}>
              <button type="button" disabled={busy || value.mappedMarketId === value.marketId}
                onClick={remember}>Use this market for future events in {value.place.locality}</button>
              <small style={{ display: 'block', marginTop: 4 }}>
                Saving a grouping also moves existing automatically assigned events from this place.
              </small>
            </div>
          )}
        </>
      )}
      {error && <p role="alert" style={{ color: '#b00020' }}>{error}</p>}
    </fieldset>
  );
}
